import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Plus, Copy, Ban, Trash2, BookOpen, ShieldAlert, Loader2, Webhook, Send, RefreshCw, Mail, ShoppingCart, Download, Activity, FilePlus2 } from "lucide-react";
import EmailQuoteDialog from "@/components/workspace/EmailQuoteDialog";

const BASE = process.env.REACT_APP_BACKEND_URL;

function dateShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function copyText(text, label = "Copied") {
  navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error("Copy failed"));
}

function Code({ children, testid }) {
  return (
    <div className="group relative">
      <pre data-testid={testid} className="overflow-x-auto rounded-md border border-border bg-black/40 p-3 text-[11px] leading-relaxed text-[hsl(var(--success))]">
        {children}
      </pre>
      <button
        onClick={() => copyText(children, "Snippet copied")}
        className="absolute right-2 top-2 rounded bg-secondary p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
        title="Copy"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Endpoint({ method, path, title, children }) {
  const color = method === "GET" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]";
  return (
    <div className="border border-border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${color}`}>{method}</span>
        <code className="text-sm font-semibold">{path}</code>
        <span className="ml-auto text-xs text-muted-foreground">{title}</span>
      </div>
      <div className="space-y-3 p-4 text-sm">{children}</div>
    </div>
  );
}

function CreateKeyDialog({ open, onOpenChange, onCreated }) {
  const [appName, setAppName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [rawKey, setRawKey] = useState(null);

  const close = (v) => {
    if (!v) { setRawKey(null); setAppName(""); setDescription(""); }
    onOpenChange(v);
  };

  const create = async () => {
    if (!appName.trim()) return toast.error("Enter the app name (e.g. PHS Pallets).");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { app_name: appName.trim(), description });
      setRawKey(data.api_key);
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="create-key-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-[hsl(var(--primary))]" /> Create API Key</DialogTitle>
          <DialogDescription>Generate a key for one of your apps. The quote source will show this app name in Commander.</DialogDescription>
        </DialogHeader>
        {!rawKey ? (
          <>
            <div className="space-y-3">
              <div>
                <Label className="label-caps">App Name</Label>
                <Input data-testid="key-app-name" value={appName} onChange={(e) => setAppName(e.target.value)} placeholder="PHS Pallets" className="mt-1.5 h-10" />
              </div>
              <div>
                <Label className="label-caps">Description (optional)</Label>
                <Input data-testid="key-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Pallet configurator app" className="mt-1.5 h-10" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="key-create-submit" onClick={create} disabled={creating || !appName.trim()}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Key"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-[hsl(var(--warning))]">
              <ShieldAlert className="h-4 w-4 shrink-0" /> Copy this key now — it will never be shown again.
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="raw-api-key" className="flex-1 break-all rounded-md border border-border bg-black/40 p-3 text-xs text-[hsl(var(--success))]">{rawKey}</code>
              <Button data-testid="copy-api-key" variant="outline" size="icon" onClick={() => copyText(rawKey, "API key copied")} title="Copy key">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Send it on every request in the <code className="font-semibold">X-API-Key</code> header. Store it in the consuming app's environment variables — never in source code.</p>
            <DialogFooter>
              <Button data-testid="key-dialog-done" onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WebhookDialog({ open, onOpenChange, apiKey, onSaved }) {
  const [url, setUrl] = useState("");
  const [allQuotes, setAllQuotes] = useState(false);
  const [secret, setSecret] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open && apiKey) {
      setUrl(apiKey.webhook_url || "");
      setAllQuotes(!!apiKey.webhook_all_quotes);
      setSecret(apiKey.webhook_secret || "");
      setSavedUrl(apiKey.webhook_url || "");
    }
  }, [open, apiKey]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/api-keys/${apiKey.id}/webhook`, {
        webhook_url: url.trim(),
        webhook_all_quotes: allQuotes,
      });
      setSecret(data.webhook_secret || "");
      setSavedUrl(data.webhook_url || "");
      toast.success(url.trim() ? "Webhook saved." : "Webhook removed.");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/api-keys/${apiKey.id}/webhook/test`);
      if (data.ok) toast.success(`Test delivered — HTTP ${data.status_code}`);
      else toast.error(`Test failed — ${data.error || `HTTP ${data.status_code}`}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };

  if (!apiKey) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="webhook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Webhook className="h-5 w-5 text-[hsl(var(--primary))]" /> Webhook — {apiKey.app_name}</DialogTitle>
          <DialogDescription>Commander POSTs a signed event to this URL whenever a quote's status changes (e.g. quoted → won).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Webhook URL</Label>
            <Input data-testid="webhook-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://pallets.phsinc.com/api/commander-webhook" className="mt-1.5 h-10" />
          </div>
          <label className="flex cursor-pointer items-center justify-between rounded-md border border-border px-3 py-2.5">
            <span className="text-sm">
              <span className="font-semibold">Send events for all quotes</span>
              <span className="block text-xs text-muted-foreground">Off = only quotes created by this app's API key.</span>
            </span>
            <Switch data-testid="webhook-all-quotes" checked={allQuotes} onCheckedChange={setAllQuotes} />
          </label>
          {secret && (
            <div>
              <Label className="label-caps">Signing Secret</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <code data-testid="webhook-secret" className="flex-1 break-all rounded-md border border-border bg-black/40 p-2.5 text-xs text-[hsl(var(--success))]">{secret}</code>
                <Button variant="outline" size="icon" onClick={() => copyText(secret, "Secret copied")} title="Copy secret"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Verify <code>X-PHS-Signature</code> = <code>sha256=HMAC_SHA256(body, secret)</code>.</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {savedUrl && (
            <Button data-testid="webhook-test-button" variant="outline" onClick={sendTest} disabled={testing} className="gap-1.5">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
            </Button>
          )}
          <Button data-testid="webhook-save-button" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Webhook"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SenderDialog({ open, onOpenChange, apiKey, onSaved }) {
  const [options, setOptions] = useState([]);
  const [senderId, setSenderId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSenderId(apiKey?.sender_user_id || "");
      api.get("/admin/api-keys/senders/options").then((r) => setOptions(r.data)).catch((e) => toast.error(apiError(e)));
    }
  }, [open, apiKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/api-keys/${apiKey.id}/sender`, { sender_user_id: senderId === "none" ? "" : senderId });
      toast.success(senderId && senderId !== "none" ? "Email sender saved." : "Email sender cleared.");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!apiKey) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sender-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Mail className="h-5 w-5 text-[hsl(var(--primary))]" /> Email Sender — {apiKey.app_name}</DialogTitle>
          <DialogDescription>
            Quote emails triggered via <code>POST /api/v1/quotes/{"{id}"}/email</code> are sent from this rep's connected Gmail account.
          </DialogDescription>
        </DialogHeader>
        <Select value={senderId || "none"} onValueChange={setSenderId}>
          <SelectTrigger data-testid="sender-select" className="h-10"><SelectValue placeholder="Choose a rep" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— No sender (email disabled) —</SelectItem>
            {options.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name || u.email}{u.gmail_connected ? "" : "  (Gmail not connected)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Reps marked "Gmail not connected" must connect Google from their Profile before the API can send as them.</p>
        <DialogFooter>
          <Button data-testid="sender-save-button" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Sender"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResellerDialog({ open, onOpenChange, apiKey, onSaved }) {
  const [form, setForm] = useState({ reseller: false, reseller_markup_pct: 0, reseller_flat_fee: 0, reseller_min_margin_pct: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && apiKey) setForm({
      reseller: !!apiKey.reseller,
      reseller_markup_pct: apiKey.reseller_markup_pct || 0,
      reseller_flat_fee: apiKey.reseller_flat_fee || 0,
      reseller_min_margin_pct: apiKey.reseller_min_margin_pct || 0,
    });
  }, [open, apiKey]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/api-keys/${apiKey.id}/reseller`, {
        reseller: form.reseller,
        reseller_markup_pct: Number(form.reseller_markup_pct) || 0,
        reseller_flat_fee: Number(form.reseller_flat_fee) || 0,
        reseller_min_margin_pct: Number(form.reseller_min_margin_pct) || 0,
      });
      toast.success(form.reseller ? "Reseller pricing saved." : "Reseller pricing disabled.");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  if (!apiKey) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reseller-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><ShoppingCart className="h-5 w-5 text-[hsl(var(--primary))]" /> Reseller Pricing — {apiKey.app_name}</DialogTitle>
          <DialogDescription>Turn this key into a sub-broker (Partner API). Their markup applies on top of your all-in PHS price; the API returns their resale price and hides your cost/margin.</DialogDescription>
        </DialogHeader>
        <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span className="text-sm font-semibold">Enable reseller pricing</span>
          <Switch checked={form.reseller} onCheckedChange={(v) => setF("reseller", v)} data-testid="reseller-enable" />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="label-caps">Markup %</Label><Input type="number" value={form.reseller_markup_pct} onChange={(e) => setF("reseller_markup_pct", e.target.value)} disabled={!form.reseller} className="mt-1.5 h-10" data-testid="reseller-markup" /></div>
          <div><Label className="label-caps">Flat Fee $</Label><Input type="number" value={form.reseller_flat_fee} onChange={(e) => setF("reseller_flat_fee", e.target.value)} disabled={!form.reseller} className="mt-1.5 h-10" data-testid="reseller-flat" /></div>
          <div><Label className="label-caps">Min Margin %</Label><Input type="number" value={form.reseller_min_margin_pct} onChange={(e) => setF("reseller_min_margin_pct", e.target.value)} disabled={!form.reseller} className="mt-1.5 h-10" data-testid="reseller-minmargin" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Example: your all-in price $500 + 12% markup → partner resells at $560. Their <code>/api/v1/rates</code> &amp; <code>/quotes</code> responses show <code>your_cost</code> and <code>your_price</code> only.</p>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="reseller-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeysTab() {
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [webhookKey, setWebhookKey] = useState(null);
  const [senderKey, setSenderKey] = useState(null);
  const [resellerKey, setResellerKey] = useState(null);

  const load = () => api.get("/admin/api-keys").then((r) => setKeys(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    try {
      await api.post(`/admin/api-keys/${k.id}/revoke`);
      toast.success(`Key for ${k.app_name} revoked.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (k) => {
    try {
      await api.delete(`/admin/api-keys/${k.id}`);
      toast.success(`Key for ${k.app_name} deleted.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">One key per consuming app. Quotes created through the API are tagged with the app name.</p>
        <Button data-testid="new-api-key-button" onClick={() => setShowCreate(true)} className="gap-1.5"><Plus className="h-4 w-4" /> New API Key</Button>
      </div>
      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>App</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Webhook</TableHead>
              <TableHead>Email Sender</TableHead>
              <TableHead>Reseller</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.length === 0 && (
              <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">
                <KeyRound className="mx-auto mb-2 h-7 w-7 opacity-40" /> No API keys yet. Create one to connect an app.
              </TableCell></TableRow>
            )}
            {keys.map((k) => (
              <TableRow key={k.id} className="border-border" data-testid={`api-key-row-${k.app_name.replace(/\s+/g, "-").toLowerCase()}`}>
                <TableCell>
                  <div className="text-sm font-semibold">{k.app_name}</div>
                  {k.description && <div className="text-xs text-muted-foreground">{k.description}</div>}
                </TableCell>
                <TableCell><code className="text-xs text-muted-foreground">{k.key_prefix}</code></TableCell>
                <TableCell>
                  <Badge variant={k.active ? "default" : "destructive"} className={k.active ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15" : ""}>
                    {k.active ? "Active" : "Revoked"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <button
                    data-testid={`webhook-config-${k.id}`}
                    onClick={() => setWebhookKey(k)}
                    title={k.webhook_url ? `Webhook: ${k.webhook_url}` : "Configure webhook"}
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition-colors ${
                      k.webhook_url
                        ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25"
                        : "text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    <Webhook className="h-3.5 w-3.5" />
                    {k.webhook_url ? (() => { try { return new URL(k.webhook_url).host; } catch { return "configured"; } })() : "Set up"}
                  </button>
                </TableCell>
                <TableCell>
                  <button
                    data-testid={`sender-config-${k.id}`}
                    onClick={() => setSenderKey(k)}
                    title={k.sender_name ? `Quote emails send as ${k.sender_name}` : "Assign an email sender"}
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition-colors ${
                      k.sender_user_id
                        ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/25"
                        : "text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {k.sender_name || "Set up"}
                  </button>
                </TableCell>
                <TableCell>
                  <button
                    data-testid={`reseller-config-${k.id}`}
                    onClick={() => setResellerKey(k)}
                    title={k.reseller ? `Reseller markup ${k.reseller_markup_pct}%` : "Configure reseller pricing"}
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition-colors ${
                      k.reseller
                        ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/25"
                        : "text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    <ShoppingCart className="h-3.5 w-3.5" />
                    {k.reseller ? `+${k.reseller_markup_pct}%` : "Off"}
                  </button>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{dateShort(k.created_at)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{dateShort(k.last_used_at)}</TableCell>
                <TableCell className="metric-num text-right text-sm">{k.request_count || 0}</TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    {k.active && (
                      <button data-testid={`revoke-key-${k.id}`} onClick={() => revoke(k)} title="Revoke key" className="rounded p-1.5 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10">
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                    <button data-testid={`delete-key-${k.id}`} onClick={() => remove(k)} title="Delete key" className="rounded p-1.5 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CreateKeyDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load} />
      <WebhookDialog open={!!webhookKey} onOpenChange={(v) => !v && setWebhookKey(null)} apiKey={webhookKey} onSaved={load} />
      <SenderDialog open={!!senderKey} onOpenChange={(v) => !v && setSenderKey(null)} apiKey={senderKey} onSaved={load} />
      <ResellerDialog open={!!resellerKey} onOpenChange={(v) => !v && setResellerKey(null)} apiKey={resellerKey} onSaved={load} />
    </div>
  );
}

function DeliveriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/api-keys/deliveries/log")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="webhook-log">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Last 50 webhook deliveries — status changes and test events.</p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="refresh-deliveries">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>Time</TableHead>
              <TableHead>App</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Quote</TableHead>
              <TableHead>URL</TableHead>
              <TableHead className="text-center">Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                <Webhook className="mx-auto mb-2 h-7 w-7 opacity-40" /> No deliveries yet. Configure a webhook on a key, then change a quote's status.
              </TableCell></TableRow>
            )}
            {rows.map((d) => (
              <TableRow key={d.id} className="border-border">
                <TableCell className="text-xs text-muted-foreground">{dateShort(d.created_at)}</TableCell>
                <TableCell className="text-sm font-semibold">{d.app_name}</TableCell>
                <TableCell><code className="text-xs">{d.event}</code></TableCell>
                <TableCell className="text-sm">{d.quote_number || "—"}</TableCell>
                <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground" title={d.url}>{d.url}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={d.ok ? "default" : "destructive"} className={d.ok ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15" : ""} title={d.error || ""}>
                    {d.ok ? `OK · ${d.status_code}` : (d.error || "Failed")}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function DocsTab() {
  return (
    <div className="max-w-4xl space-y-5" data-testid="api-docs">
      <div className="border border-border bg-card p-4 text-sm">
        <h3 className="font-heading text-base font-bold">Authentication</h3>
        <p className="mt-1 text-muted-foreground">
          Every request needs an <code className="font-semibold text-foreground">X-API-Key</code> header with a key generated on the Keys tab.
          Base URL: <code className="font-semibold text-foreground">{BASE}</code>
        </p>
        <div className="mt-3">
          <Code testid="doc-ping">{`curl "${BASE}/api/v1/ping" \\
  -H "X-API-Key: phs_live_YOUR_KEY"`}</Code>
        </div>
      </div>

      <Endpoint method="GET" path="/api/v1/products" title="Catalog products for stacking by product_id">
        <Code>{`curl "${BASE}/api/v1/products?search=EXP" -H "X-API-Key: phs_live_YOUR_KEY"`}</Code>
        <p className="text-xs text-muted-foreground">Returns id, name, category, dims (in), weight (lb), nesting height, stack limits. Use the <code>id</code> in catalog line items below.</p>
      </Endpoint>

      <Endpoint method="POST" path="/api/v1/stack" title="Stack calculator + freight class (no rates, nothing saved)">
        <p className="text-xs text-muted-foreground">Option A — catalog product:</p>
        <Code>{`curl -X POST "${BASE}/api/v1/stack" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{"product_id": "PRODUCT_ID", "quantity": 100}'`}</Code>
        <p className="text-xs text-muted-foreground">Option B — manual specs (dims per unit, weight per unit or total):</p>
        <Code>{`curl -X POST "${BASE}/api/v1/stack" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{"line_items": [{"mode": "manual", "units": 2, "pieces": 2, "weight": 500,
       "weight_is_total": true, "length": 48, "width": 40, "height": 48,
       "description": "Plastic pallets"}]}'`}</Code>
        <p className="text-xs text-muted-foreground">Returns stack configuration, density, and NMFC freight class per item plus shipment totals.</p>
      </Endpoint>

      <Endpoint method="POST" path="/api/v1/rates" title="Live carrier rate shopping (calculation only)">
        <Code>{`curl -X POST "${BASE}/api/v1/rates" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{
    "origin_zip": "60173", "destination_zip": "30301",
    "line_items": [
      {"mode": "catalog", "product_id": "PRODUCT_ID", "quantity": 100},
      {"mode": "manual", "units": 1, "weight": 800, "weight_is_total": true,
       "length": 48, "width": 40, "height": 60, "description": "Crated parts"}
    ],
    "accessorials": {"liftgate_delivery": true}
  }'`}</Code>
        <p className="text-xs text-muted-foreground">Returns all live carrier rates (cost + suggested sell price + margin), resolved line items, class, and the recommended (cheapest) carrier. Nothing is saved. Optionally include <code>customer_name</code>, <code>dest_contact_email</code>, <code>dest_contact_phone</code> — the rate shop is then captured as a contactable lead in the Rate Shopping tab, where reps can convert it to a draft quote with one click.</p>
      </Endpoint>

      <Endpoint method="POST" path="/api/v1/quotes" title="Rate AND save the quote into Commander">
        <Code>{`curl -X POST "${BASE}/api/v1/quotes" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "ACME CO",
    "origin_zip": "60173", "destination_zip": "30301",
    "line_items": [{"mode": "manual", "units": 2, "pieces": 2, "weight": 500,
      "weight_is_total": true, "length": 48, "width": 40, "height": 48,
      "description": "Plastic pallets"}]
  }'`}</Code>
        <p className="text-xs text-muted-foreground">
          Same body as <code>/rates</code> plus optional <code>customer_name</code>, <code>quote_ref</code>, <code>sales_rep</code>, <code>pickup_date</code>, <code>notes</code>, <code>declared_value</code>.
          The quote is saved with status <code>quoted</code>, appears in Quote History / Live Map / Insights, and is tagged with your app name as the source.
          Response includes <code>quote_number</code> (e.g. PHS-000051) and the full rate comparison.
        </p>
      </Endpoint>

      <Endpoint method="GET" path={"/api/v1/quotes/{id or quote_number}"} title="Quote lookup">
        <Code>{`curl "${BASE}/api/v1/quotes/PHS-000051" -H "X-API-Key: phs_live_YOUR_KEY"`}</Code>
        <p className="text-xs text-muted-foreground">Fetch a saved quote by its id or quote number — includes stored rates, financials, and status.</p>
      </Endpoint>

      <Endpoint method="POST" path={"/api/v1/quotes/{id or quote_number}/email"} title="Email the branded PDF quote to the customer">
        <Code>{`curl -X POST "${BASE}/api/v1/quotes/PHS-000051/email" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{
    "to": "buyer@customer.com",
    "message": "Hi! Your freight quote is attached.",
    "include_accept_link": true
  }'`}</Code>
        <p className="text-xs text-muted-foreground">
          Sends the customer PDF (with street-view + route map) from the Gmail account of the rep assigned as this key's
          <span className="font-semibold text-foreground"> Email Sender</span> (Keys tab). Optional <code>subject</code> override.
          <code> include_accept_link</code> adds an "Accept this Quote" button — when clicked, the quote flips to <code>won</code> and your webhook fires.
          Returns 428 if no sender is configured or the sender hasn't connected Google.
        </p>
      </Endpoint>

      <Endpoint method="POST" path={"/api/v1/quotes/{id or quote_number}/book"} title="Book the shipment (GlobalTranz BOL or R+L BOL)">
        <p className="text-xs text-muted-foreground">GlobalTranz rate selected on the quote (auto-detected):</p>
        <Code>{`curl -X POST "${BASE}/api/v1/quotes/PHS-000051/book" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{
    "globaltranz": {
      "origin_street": "123 Warehouse Dr", "origin_company": "Premier Handling Solutions",
      "origin_contact_name": "Dock Office", "origin_phone": "8475551234",
      "dest_street": "500 Receiving Ln", "dest_contact_name": "Jane Buyer", "dest_phone": "4045559876"
    }
  }'`}</Code>
        <p className="text-xs text-muted-foreground">R+L Carriers selected — generates the real R+L BOL, and can schedule the actual pickup (dispatches a truck) via <code>schedule_pickup</code>:</p>
        <Code>{`curl -X POST "${BASE}/api/v1/quotes/PHS-000051/book" \\
  -H "X-API-Key: phs_live_YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{"method": "rl", "rl": {"shipper_name": "PHS", "consignee_name": "ACME CO",
       "schedule_pickup": true, "pickup_date": "2026-07-15",
       "pickup_ready_time": "09:00", "pickup_close_time": "17:00",
       "pickup_contact_name": "Dock Office", "pickup_contact_phone": "8475551234"}}'`}</Code>
        <p className="text-xs text-muted-foreground">
          The booking method is auto-detected from the quote's selected carrier — pass <code>method</code> to force it.
          GlobalTranz returns <code>bol_number</code>, carrier, cost, and ETA; R+L returns <code>pro_number</code> and a
          <code> bol_pdf_url</code>. The quote flips to <code>booked</code> and appears on the Shipments board.
          409 = already booked · 428 = integration not configured.
        </p>
      </Endpoint>

      <Endpoint method="GET" path={"/api/v1/quotes/{id or quote_number}/tracking"} title="Live tracking for a booked load">
        <Code>{`curl "${BASE}/api/v1/quotes/PHS-000051/tracking" -H "X-API-Key: phs_live_YOUR_KEY"`}</Code>
        <Code>{`{
  "quote_number": "PHS-000051", "bol_type": "GlobalTranz", "bol_number": "9034112",
  "carrier": "Estes Express", "estimated_delivery": "2026-07-10", "delivered": false,
  "tracking": {"status": "In Transit", "current_location": "ATLANTA, GA",
               "pro_number": "123-456789", "last_event": {"time": "…", "status": "…", "city": "…", "state": "…"}}
}`}</Code>
        <p className="text-xs text-muted-foreground">
          Works for both R+L (PRO/BOL trace) and GlobalTranz loads. Each call pulls fresh carrier scans.
          When a load first reports delivered, Commander fires the <code>shipment.delivered</code> webhook and posts to the team's #dispatch channel.
          404 = quote not booked yet.
        </p>
      </Endpoint>

      <Endpoint method="GET" path={"/api/v1/quotes/{id or quote_number}/bol.pdf"} title="Download the R+L Bill of Lading PDF">
        <Code>{`curl -o BOL.pdf "${BASE}/api/v1/quotes/PHS-000051/bol.pdf" -H "X-API-Key: phs_live_YOUR_KEY"`}</Code>
        <p className="text-xs text-muted-foreground">Returns the generated R+L BOL as a PDF. 404 if no R+L BOL exists on the quote.</p>
      </Endpoint>

      <div className="border border-border bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <span className="rounded bg-[hsl(var(--warning))]/15 px-2 py-0.5 text-[11px] font-bold text-[hsl(var(--warning))]">WEBHOOK</span>
          <code className="text-sm font-semibold">quote.created · quote.status_changed · shipment.tracking_updated · shipment.delivered</code>
          <span className="ml-auto text-xs text-muted-foreground">Commander calls YOUR app on quote events</span>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <p className="text-xs text-muted-foreground">
            Set a webhook URL on your API key (Keys tab). Commander POSTs <code>quote.created</code> the moment a quote is saved
            (via the API or by a rep in the workspace), <code>quote.status_changed</code> whenever a quote's status changes
            (e.g. quoted → won via the customer accept link or a rep), <code>shipment.tracking_updated</code> whenever a booked
            load's carrier status changes (great for live UI / Apple Live Activities), and <code>shipment.delivered</code> the
            moment a booked load first reports delivered (both include a <code>shipment</code> object with BOL, carrier, status,
            location, ETA and last scan).
            By default only quotes created by your app trigger events;
            enable "all quotes" on the key to receive everything. <code>quote.created</code> has no <code>previous_status</code> field.
          </p>
          <Code testid="doc-webhook-payload">{`POST <your webhook URL>
Headers:
  Content-Type: application/json
  X-PHS-Event: quote.status_changed
  X-PHS-Signature: sha256=<HMAC-SHA256 hex of raw body, keyed with your signing secret>

{
  "event": "quote.status_changed",
  "timestamp": "2026-07-06T17:02:11+00:00",
  "previous_status": "quoted",
  "quote": {
    "id": "…", "quote_number": "PHS-000051", "status": "won",
    "customer_name": "ACME CO", "quote_ref": "PALLETS-001",
    "origin_zip": "60173", "destination_zip": "30301",
    "selected_carrier_code": "P1_DYLT", "carrier_cost": 275.54,
    "sell_price": 379.43, "margin_pct": 27.38,
    "source": "api", "source_app": "PHS Pallets",
    "created_by": "PHS Pallets (API)", "created_at": "…", "updated_at": "…"
  }
}`}</Code>
          <p className="text-xs text-muted-foreground">Verify the signature (Python example):</p>
          <Code testid="doc-webhook-verify">{`import hmac, hashlib
expected = "sha256=" + hmac.new(SECRET.encode(), raw_body, hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, request.headers["X-PHS-Signature"])`}</Code>
          <p className="text-xs text-muted-foreground">Respond with any 2xx within 10s. Failed deliveries retry 3× with backoff — see the Webhook Log tab. Use "Send Test" on the key to verify your receiver.</p>
        </div>
      </div>

      <div className="border border-border bg-card p-4 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">Accessorial flags</span> (all boolean, all optional): liftgate_pickup, liftgate_delivery, residential_pickup, residential_delivery, limited_access_pickup, limited_access_delivery, appointment_required, inside_delivery, notify_before_delivery, hazmat, over_dimensional.
        <br /><span className="font-semibold text-foreground">Errors:</span> 401 = missing/invalid/revoked key · 400 = invalid body · 404 = not found. Live carrier calls can take 10–30 seconds; set client timeouts to 60s.
      </div>
    </div>
  );
}

export default function ApiAccess() {
  return (
    <div className="space-y-5 animate-fade-up" data-testid="api-access-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">API Access</h1>
        <p className="mt-1 text-sm text-muted-foreground">Connect your other PHS apps to the Commander engine — stack calc, live rates, and quote creation.</p>
      </div>
      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys" data-testid="tab-keys" className="gap-1.5"><KeyRound className="h-4 w-4" /> API Keys</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs" className="gap-1.5"><BookOpen className="h-4 w-4" /> Documentation</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="tab-deliveries" className="gap-1.5"><Webhook className="h-4 w-4" /> Webhook Log</TabsTrigger>
          <TabsTrigger value="rateshops" data-testid="tab-rateshops" className="gap-1.5"><Activity className="h-4 w-4" /> Rate Shopping</TabsTrigger>
          <TabsTrigger value="woocommerce" data-testid="tab-woocommerce" className="gap-1.5"><ShoppingCart className="h-4 w-4" /> WooCommerce</TabsTrigger>
        </TabsList>
        <TabsContent value="keys" className="mt-4"><KeysTab /></TabsContent>
        <TabsContent value="docs" className="mt-4"><DocsTab /></TabsContent>
        <TabsContent value="deliveries" className="mt-4"><DeliveriesTab /></TabsContent>
        <TabsContent value="rateshops" className="mt-4"><RateShopsTab /></TabsContent>
        <TabsContent value="woocommerce" className="mt-4"><WooTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function RateShopsTab() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState("7");
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [emailTo, setEmailTo] = useState("");

  const load = async (d = days) => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/rate-shops/summary"),
        api.get("/rate-shops", { params: { days: d } }),
      ]);
      setSummary(s.data);
      setRows(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onConverted = (data) => {
    toast.success(
      `Draft quote ${data.quote_number} created${data.customer_created ? " — customer profile added" : ""}`,
      { action: { label: "Quote History", onClick: () => { window.location.href = "/history"; } } }
    );
    load();
  };

  const convertShop = async (id) => {
    setConverting(id);
    try {
      const { data } = await api.post(`/rate-shops/${id}/convert`);
      onConverted(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConverting(null);
    }
  };

  const convertLane = async (lane) => {
    const key = `lane-${lane.origin_zip}-${lane.destination_zip}`;
    setConverting(key);
    try {
      const { data } = await api.post("/rate-shops/convert-lane", {
        origin_zip: lane.origin_zip, destination_zip: lane.destination_zip,
      });
      onConverted(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConverting(null);
    }
  };

  const openEmailForQuote = async (data) => {
    const { data: q } = await api.get(`/quotes/${data.quote_id}`);
    setEmailQuote(q);
    setEmailTo(data.customer_email || "");
    setEmailOpen(true);
  };

  const emailShop = async (r) => {
    setConverting(`email-${r.id}`);
    try {
      const { data } = await api.post(`/rate-shops/${r.id}/convert`);
      toast.success(`Draft quote ${data.quote_number} created — ready to email`);
      await openEmailForQuote(data);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConverting(null);
    }
  };

  const emailLane = async (lane) => {
    setConverting(`email-lane-${lane.origin_zip}-${lane.destination_zip}`);
    try {
      const { data } = await api.post("/rate-shops/convert-lane", {
        origin_zip: lane.origin_zip, destination_zip: lane.destination_zip,
      });
      toast.success(`Draft quote ${data.quote_number} created — ready to email`);
      await openEmailForQuote(data);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConverting(null);
    }
  };

  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-5" data-testid="rateshops-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Today", summary?.today ?? "—", "rate requests"],
          ["Last 7 days", summary?.last_7_days ?? "—", "rate requests"],
          ["Conversion", summary ? `${summary.conversion_pct_7d}%` : "—", "shopped → ordered (7d)"],
          ["Stores active", summary?.per_app?.length ?? "—", "last 7 days"],
        ].map(([label, val, sub]) => (
          <div key={label} className="border border-border bg-card p-4">
            <div className="label-caps text-muted-foreground">{label}</div>
            <div className="metric-num mt-1 text-2xl font-black">{val}</div>
            <div className="text-[11px] text-muted-foreground">{sub}</div>
          </div>
        ))}
      </div>

      {!!summary?.per_app?.length && (
        <div className="flex flex-wrap gap-2">
          {summary.per_app.map((a) => (
            <Badge key={a.app} variant="outline" className="gap-1.5 px-3 py-1 text-xs">
              <span className="font-bold">{a.app}</span>
              <span className="metric-num">{a.count}</span>
              <span className="text-[hsl(var(--success))]">{a.converted} ordered</span>
            </Badge>
          ))}
        </div>
      )}

      {!!summary?.abandoned_lanes?.length && (
        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <span className="font-heading text-sm font-bold">Lead opportunities — priced but never ordered (30d)</span>
            <p className="text-xs text-muted-foreground">Lanes customers rate-shopped without completing checkout. Warm leads for your reps.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lane</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Times shopped</TableHead>
                <TableHead className="text-right">Avg price shown</TableHead>
                <TableHead>Store(s)</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.abandoned_lanes.map((l, i) => (
                <TableRow key={i} data-testid={`abandoned-lane-${i}`}>
                  <TableCell className="metric-num text-sm font-bold">{l.origin_zip} → {l.destination_zip}</TableCell>
                  <TableCell className="text-xs">{(l.customers || []).length ? l.customers.join(", ") : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="metric-num text-right">{l.count}</TableCell>
                  <TableCell className="metric-num text-right">${(l.avg_sell || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(l.apps || []).join(", ")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtTime(l.last_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {!!(l.emails || []).length && (
                        <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs"
                          disabled={converting === `email-lane-${l.origin_zip}-${l.destination_zip}`}
                          onClick={() => emailLane(l)}
                          data-testid={`email-lane-${i}`}>
                          {converting === `email-lane-${l.origin_zip}-${l.destination_zip}`
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Mail className="h-3 w-3" />} Email Quote
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs"
                        disabled={converting === `lane-${l.origin_zip}-${l.destination_zip}`}
                        onClick={() => convertLane(l)}
                        data-testid={`convert-lane-${i}`}>
                        {converting === `lane-${l.origin_zip}-${l.destination_zip}`
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <FilePlus2 className="h-3 w-3" />} Create Quote
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="border border-border bg-card">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="font-heading text-sm font-bold">Recent rate requests</span>
          <Select value={days} onValueChange={(v) => { setDays(v); load(v); }}>
            <SelectTrigger className="ml-auto h-8 w-32 text-xs" data-testid="rateshops-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => load()} data-testid="rateshops-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="rateshops-empty">
            No rate requests yet — they'll appear here the moment a store checkout calls /api/v1/rates.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Lane</TableHead>
                <TableHead className="text-right">Weight</TableHead>
                <TableHead className="text-right">Class</TableHead>
                <TableHead className="text-right">Best price</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-muted-foreground">{fmtTime(r.at)}</TableCell>
                  <TableCell className="text-sm font-semibold">{r.app_name}</TableCell>
                  <TableCell>
                    {r.customer_name || r.customer_email || r.customer_phone ? (
                      <div data-testid={`rateshop-customer-${r.id}`}>
                        <div className="text-sm font-semibold">{r.customer_name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {[r.customer_email, r.customer_phone].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Anonymous</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="metric-num text-sm">{r.origin_zip} → {r.destination_zip}</div>
                    {!!(r.accessorials || []).length && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {r.accessorials.map((a) => (
                          <span key={a} className="rounded bg-[hsl(var(--primary))]/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
                            {a.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="metric-num text-right text-sm">{r.total_weight ? `${Math.round(r.total_weight)} lb` : "—"}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{r.calculated_class || "—"}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{r.best_sell ? `$${r.best_sell.toFixed(2)}` : "—"}</TableCell>
                  <TableCell>
                    {r.converted ? (
                      <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15">{r.quote_number || "Ordered"}</Badge>
                    ) : r.can_convert ? (
                      <div className="flex items-center gap-1.5">
                        {!!r.customer_email && (
                          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs"
                            disabled={converting === `email-${r.id}`}
                            onClick={() => emailShop(r)}
                            data-testid={`email-shop-${r.id}`}>
                            {converting === `email-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Email Quote
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs"
                          disabled={converting === r.id}
                          onClick={() => convertShop(r.id)}
                          data-testid={`convert-shop-${r.id}`}>
                          {converting === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FilePlus2 className="h-3 w-3" />} Create Quote
                        </Button>
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Shopped</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <EmailQuoteDialog open={emailOpen} onOpenChange={setEmailOpen} quote={emailQuote} defaultTo={emailTo} />
    </div>
  );
}

function WooTab() {
  const steps = [
    ["Download & install", "Download the plugin zip below, then in WordPress admin: Plugins → Add New → Upload Plugin → activate."],
    ["Create an API key", "In the API Keys tab here, create a key for the store (e.g. \"PHS Pallets Store\"). Copy the phs_… key — it's shown only once."],
    ["Connect the store", "WooCommerce → Settings → Shipping → PHS Freight (LTL): paste the Commander URL, API key, and the store's origin (warehouse) ZIP."],
    ["Add to shipping zones", "WooCommerce → Settings → Shipping → your zone → Add shipping method → PHS Freight (LTL)."],
    ["Set product weights", "Each product needs a Weight (Shipping tab). Dimensions recommended — defaults of 48×40×48 in apply if missing. Optional per-product custom field _phs_freight_class pins a class; otherwise Commander computes it from density."],
    ["Delivery accessorials (v1.5)", "Shoppers see \"Residential address\" and \"Liftgate needed at delivery\" checkboxes at checkout — ticking them re-runs rates live with the fees included. Works on BOTH the new block-based checkout and the classic shortcode checkout. Residential auto-checks when there's no company name. Configure which options show (plus always-on accessorials) in the plugin settings."],
    ["Buyer tracking links (optional)", "On the API key here, set the Webhook URL to https://YOUR-STORE.com/wp-json/phs-freight/v1/webhook and copy the whsec_… secret into the plugin. Once loads book, buyers get a \"Track your freight\" button on their order page + order emails, with delivery notes posted automatically."],
    ["Accurate shipped/delivered emails (v1.7)", "Booking a BOL no longer emails the buyer or completes the order (it only adds a private note) — so backordered items are never announced as shipped. The buyer's \"your freight shipped\" email now fires only when the carrier actually picks it up, and the \"delivered\" email + auto-completion only on a carrier-confirmed delivery. Re-upload this v1.7 zip to replace any older version on your store."],
  ];
  return (
    <div className="max-w-3xl space-y-5" data-testid="woo-tab">
      <div className="border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <ShoppingCart className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span className="font-heading text-base font-bold">WooCommerce Plugin</span>
          <span className="rounded-full border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-2 py-0.5 text-[11px] font-bold text-[hsl(var(--primary))]" data-testid="woo-plugin-version">v1.7.0</span>
          <span className="text-xs text-muted-foreground">live LTL rates at checkout · auto quote creation</span>
          <a href="/downloads/phs-freight-shipping.zip" download data-testid="woo-plugin-download"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--primary))] px-3.5 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90">
            <Download className="h-4 w-4" /> Download plugin v1.7.0 (.zip)
          </a>
        </div>
        <div className="space-y-4 p-4 text-sm">
          <p className="text-muted-foreground">
            Drops a <span className="font-semibold text-foreground">"PHS Freight (LTL)"</span> shipping method into any WooCommerce store.
            At checkout it calls this Commander's <code className="rounded bg-muted px-1 text-xs">/api/v1/rates</code> engine server-to-server —
            density-based freight class, live carrier shopping, and your pricing rules/margins already applied. When an order is placed it
            auto-creates a quote in Commander (order gets a note with the quote #, cost and sell). Includes rate caching and a fallback flat
            rate so checkout never breaks.
          </p>
          <ol className="space-y-3">
            {steps.map(([title, body], i) => (
              <li key={title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-xs font-bold text-[hsl(var(--primary))]">{i + 1}</span>
                <div>
                  <div className="font-semibold">{title}</div>
                  <div className="text-xs leading-relaxed text-muted-foreground">{body}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 p-3 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Troubleshooting:</span> enable Debug logging in the plugin settings, then check
            WooCommerce → Status → Logs (source <code className="rounded bg-muted px-1">phs-freight</code>). Rates only quote US 5-digit ZIPs;
            products without weight are skipped. Use "Minimum cart weight" to reserve LTL for heavy carts and let parcel methods handle small orders.
          </div>
        </div>
      </div>
    </div>
  );
}
